import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Save, RotateCcw } from 'lucide-react';
import { useDiscrepancyReview } from './hooks/useDiscrepancyReview';
import { DiscrepancyFilters } from './components/DiscrepancyFilters';
import { DiscrepancySummary } from './components/DiscrepancySummary';
import { DiscrepancyTable } from './components/DiscrepancyTable';

export const DiscrepancyReviewPage: React.FC = () => {
  const {
    items,
    summary,
    filters,
    editedQuantities,
    loading,
    applying,
    hasSearched,
    locations,
    categories,
    pendingAdjustmentsCount,
    updateFilters,
    fetchDiscrepancies,
    updateConfirmedQuantity,
    resetEdits,
    applyAdjustments,
    clearFilters,
  } = useDiscrepancyReview();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleApply = () => {
    if (pendingAdjustmentsCount > 0) {
      setShowConfirmDialog(true);
    }
  };

  const confirmApply = async () => {
    setShowConfirmDialog(false);
    await applyAdjustments();
  };

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 h-full overflow-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          Revisión de Diferencias
        </h1>
        <p className="text-gray-600 mt-1 text-sm md:text-base">
          Comparar cantidades pedidas vs recibidas en Transferencias y Órdenes de Compra
        </p>
      </div>

      {/* Filters */}
      <DiscrepancyFilters
        filters={filters}
        onFilterChange={updateFilters}
        onSearch={fetchDiscrepancies}
        onClear={clearFilters}
        locations={locations}
        categories={categories}
        loading={loading}
      />

      {/* Summary */}
      {hasSearched && !loading && (
        <DiscrepancySummary summary={summary} />
      )}

      {/* Table */}
      <DiscrepancyTable
        items={items}
        editedQuantities={editedQuantities}
        onQuantityChange={updateConfirmedQuantity}
        loading={loading}
        hasSearched={hasSearched}
      />

      {/* Action bar */}
      {hasSearched && items.length > 0 && (
        <div className="flex items-center justify-between bg-white rounded-lg border px-6 py-4">
          <div className="text-sm text-gray-500">
            {pendingAdjustmentsCount > 0 ? (
              <span className="text-amber-600 font-medium">
                {pendingAdjustmentsCount} ajuste{pendingAdjustmentsCount !== 1 ? 's' : ''} pendiente{pendingAdjustmentsCount !== 1 ? 's' : ''}
              </span>
            ) : (
              'Sin ajustes pendientes'
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={resetEdits}
              disabled={pendingAdjustmentsCount === 0}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Limpiar Cambios
            </Button>
            <Button
              onClick={handleApply}
              disabled={pendingAdjustmentsCount === 0 || applying}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Save className="h-4 w-4 mr-2" />
              {applying ? 'Aplicando...' : 'Aplicar Ajustes'}
            </Button>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirmar Ajustes
            </h3>
            <p className="text-gray-600 mb-4">
              Se aplicarán <strong>{pendingAdjustmentsCount}</strong> ajuste{pendingAdjustmentsCount !== 1 ? 's' : ''} al
              kardex. Esta acción creará movimientos de inventario que no se pueden deshacer fácilmente.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                Cancelar
              </Button>
              <Button
                onClick={confirmApply}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Confirmar y Aplicar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
